import { format, isValid, parseISO } from "date-fns";
import { DOCUMENT_FILE_EXTENSIONS, documentContentTypeOfExtension, type ApiClient, type DocumentOwner, type DocumentRecord } from "@shyne/shared/api";

type DocumentsApi = Pick<ApiClient["documents"], "list" | "upload" | "record" | "link" | "setExpiry" | "delete">;

export type PutObject = (url: string, init: RequestInit) => Promise<Response>;

export type OpenTab = () => Window | null;

export type DocumentPage = { data: DocumentRecord[]; count: number };

export const UNSUPPORTED_DOCUMENT_FILE = "Choose a PDF or an image (PNG, JPEG, WebP, GIF, HEIC or HEIF).";

const UPLOAD_REFUSED = "The document could not be uploaded. Try again.";

const EXTENSION_OF_TYPE: Record<string, string> = Object.fromEntries(
  DOCUMENT_FILE_EXTENSIONS.map((extension) => [documentContentTypeOfExtension(extension), extension]).reverse(),
);

const extensionOfName = (name: string) => (name.includes(".") ? name.slice(name.lastIndexOf(".") + 1).toLowerCase() : "");

export function documentExtensionOf(file: File): string | undefined {
  const named = extensionOfName(file.name);
  if (documentContentTypeOfExtension(named) !== undefined) return named;
  return EXTENSION_OF_TYPE[file.type];
}

export const expiresOnOf = (expiresAt: string | Date | null | undefined): string | null => {
  if (expiresAt === null || expiresAt === undefined || expiresAt === "") return null;
  return typeof expiresAt === "string" ? expiresAt : format(expiresAt, "yyyy-MM-dd");
};

export const shownDay = (day: string | null): string => {
  if (day === null) return "—";
  const parsed = parseISO(day);
  return isValid(parsed) ? format(parsed, "MM/dd/yyyy") : "—";
};

export const openNewTab: OpenTab = () => window.open("", "_blank");

export function createDocuments(api: DocumentsApi, put: PutObject, openTab: OpenTab = openNewTab) {
  return {
    async list(owner: DocumentOwner): Promise<DocumentRecord[]> {
      return (await api.list(owner)).items;
    },

    async page(owner: DocumentOwner, page: number, pageSize: number): Promise<DocumentPage> {
      const { items } = await api.list(owner);
      return { data: items.slice((page - 1) * pageSize, page * pageSize), count: items.length };
    },

    async upload(owner: DocumentOwner, file: File, expiresOn: string | null): Promise<DocumentRecord> {
      const extension = documentExtensionOf(file);
      const contentType = extension === undefined ? undefined : documentContentTypeOfExtension(extension);
      if (extension === undefined || contentType === undefined) throw new Error(UNSUPPORTED_DOCUMENT_FILE);
      const { path, uploadUrl } = await api.upload({ owner, extension });
      const answer = await put(uploadUrl, { method: "PUT", headers: { "content-type": contentType }, body: file });
      if (!answer.ok) throw new Error(UPLOAD_REFUSED);
      return api.record({ owner, path, filename: file.name, expiresOn });
    },

    async open(id: string): Promise<void> {
      const tab = openTab();
      try {
        const { url } = await api.link(id);
        if (tab === null) {
          window.open(url, "_blank", "noopener,noreferrer");
          return;
        }
        tab.opener = null;
        tab.location.href = url;
      } catch (error) {
        tab?.close();
        throw error;
      }
    },

    setExpiry: (id: string, expiresOn: string | null): Promise<DocumentRecord> => api.setExpiry(id, { expiresOn }),

    remove: async (id: string): Promise<void> => {
      await api.delete(id);
    },
  };
}

export type Documents = ReturnType<typeof createDocuments>;
